import { Injectable, signal } from '@angular/core';
import {
  clearLoginAttemptedPath,
  getLoginAttemptedPath,
  persistLoginAttemptedPath,
} from '../utils/maha-panel-access.util';

/** Build an absolute redirect URI for Keycloak login from a route path or full URL. */
export function buildAbsoluteRedirectUri(pathOrUrl: string): string {
  if (pathOrUrl.startsWith('http://') || pathOrUrl.startsWith('https://')) {
    return pathOrUrl;
  }
  const path = pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`;
  return `${window.location.origin}${path}`;
}

/** True while Keycloak is booting or processing an OAuth redirect callback. */
export function isKeycloakAuthInProgress(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  if (sessionStorage.getItem('keycloak-initializing') === 'true') {
    return true;
  }
  const hash = window.location.hash || '';
  const search = window.location.search || '';
  return (
    (hash.includes('code=') && hash.includes('state=')) ||
    (search.includes('code=') && search.includes('state='))
  );
}

/**
 * Global login modal state for unauthenticated access to protected routes.
 */
@Injectable({
  providedIn: 'root',
})
export class LoginModalService {
  readonly visible = signal(false);
  /** Keycloak redirect — return to the page the user was trying to access. */
  readonly redirectUri = signal<string | undefined>(undefined);

  open(pathOrUrl?: string): void {
    if (isKeycloakAuthInProgress()) {
      return;
    }

    if (pathOrUrl) {
      persistLoginAttemptedPath(pathOrUrl);
      this.redirectUri.set(buildAbsoluteRedirectUri(pathOrUrl));
    } else {
      clearLoginAttemptedPath();
      this.redirectUri.set(undefined);
    }

    this.visible.set(true);
  }

  getAttemptedPath(): string | undefined {
    return getLoginAttemptedPath();
  }

  clearAttemptedPath(): void {
    clearLoginAttemptedPath();
  }

  close(clearAttemptedPath = false): void {
    this.visible.set(false);
    this.redirectUri.set(undefined);
    if (clearAttemptedPath) {
      clearLoginAttemptedPath();
    }
  }
}
